import { OnboardingLayout } from "@/components/layouts/OnboardingLayout"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Small } from "@/components/ui/typography"
import { Info } from "lucide-react"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { useState } from "react"
import { atom } from "jotai"
import { Button } from "@/components/ui/button"
import { z } from "zod"
import { useForm, UseFormReturn } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Profession, PROFESSIONS } from "@/constants/professions"

const professionFormSchema = z.object({
	profession: z.string(),
})

export type ProfessionFormData = z.infer<typeof professionFormSchema>
export type ProfessionType = (typeof PROFESSIONS)[number]["id"]

type OnboardingProfessionScreenProps = {
	profession: string
	onSubmit: (value: ProfessionType) => void
	onBack: () => void
}

export function OnboardingProfessionScreen({
	profession,
	onSubmit,
	onBack,
}: OnboardingProfessionScreenProps) {
	const form = useForm<ProfessionFormData>({
		resolver: zodResolver(professionFormSchema),
		defaultValues: { profession: profession },
	})
	const activeProfession = form.watch("profession")
	return (
		<OnboardingLayout
			title="Select your profession type"
			stepper={{
				currentStep: 2,
				steps: 4,
			}}
			onNext={() => {
				form.handleSubmit((data) => {
					onSubmit(data.profession as ProfessionType)
				})()
			}}
			onBack={() => onBack()}
			description="This helps MileKeeper AI customize the tracking parameters for your business trips"
		>
			<RadioGroup
				className="flex flex-col gap-1.5"
				value={activeProfession}
				onValueChange={(id) => form.setValue("profession", id as ProfessionType)}
			>
				{PROFESSIONS.map((profession) => (
					<div
						key={profession.id}
						className={cn(
							activeProfession === profession.id
								? "outline outline-2 outline-offset-0 outline-primary"
								: "outline outline-1 outline-offset-0 outline-input",
							"flex items-center space-x-3 rounded-lg p-4 transition-colors"
						)}
					>
						<RadioGroupItem value={profession.id} id={profession.id} />
						<label
							htmlFor={profession.id}
							className="flex-1 flex items-start justify-between cursor-pointer"
						>
							<div className="">
								<Small>{profession.l10n.title}</Small>
								<Small className="text-muted-foreground font-normal">
									{profession.l10n.description}
								</Small>
							</div>
							<Dialog>
								<DialogTrigger asChild>
									<Info className="h-5 w-5 text-gray-400 flex-shrink-0 ml-4" />
								</DialogTrigger>
								<ProfessionInfoDialogContent profession={profession} />
							</Dialog>
						</label>
					</div>
				))}
			</RadioGroup>
		</OnboardingLayout>
	)
}

type ProfessionInfoDialogProps = {
	profession: Profession
}

const ProfessionInfoDialogContent: React.FC<ProfessionInfoDialogProps> = ({
	profession,
}) => {
	return (
		<DialogContent closeVisible onOpenAutoFocus={(e) => e.preventDefault()}>
			<DialogHeader>
				<DialogTitle>{profession.l10n.title}</DialogTitle>
			</DialogHeader>
			<div className="flex flex-col gap-4">
				<div>
					<Small className="font-bold">Common Characteristics:</Small>
					<Small className="font-normal">
						{profession.l10n.commonCharacteristics}
					</Small>
				</div>

				<div>
					<Small className="font-bold">Working Hours:</Small>
					<Small className="font-normal">
						{profession.l10n.workingHours.primary}{" "}
						{profession.l10n.workingHours.details}
					</Small>
				</div>

				<div>
					<Small className="font-bold">Main purpose of travel:</Small>
					<Small className="font-normal">{profession.l10n.mainPurpose}</Small>
				</div>

				<div>
					<Small className="font-bold">Professions:</Small>
					<ul className="list-decimal pl-5 text-sm font-normal leading-5">
						{profession.l10n.professions.map((prof, index) => (
							<li key={index}>{prof}</li>
						))}
					</ul>
				</div>
			</div>
		</DialogContent>
	)
}
